import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { formatPrice, getAllPriceFormats } from '../utils/currency';
import ImageSeparator from '../components/ImageSeparator';
import {
  School,
  CreditCard,
  CreditCard as IdCard,
  Stethoscope,
  Shield,
  Clock,
  Globe,
  Users,
  CheckCircle,
  ArrowRight,
  Video,
  Phone,
  MessageCircle,
  DollarSign,
  BookOpen,
  Heart,
  AlertTriangle,
  FileText
} from 'lucide-react';

const HowItWorks = () => {
  const steps = [
    {
      number: "01",
      title: "Enroll at School",
      description: `Enrollment is done directly at participating schools. Parents provide basic documents (child's birth certificate or school ID). Schools collect the annual premium of ${formatPrice('USD')} (${formatPrice('CFA')} / ${formatPrice('GMD')}) per child and securely submit it to AMHAS.`,
      details: [
        "Enrollment done directly at participating schools",
        "Parents provide basic documents (birth certificate or school ID)",
        "Schools collect annual premium and submit to AMHAS",
        "Each child receives a membership ID card"
      ],
      icon: School,
      color: "primary"
    },
    {
      number: "02",
      title: "Affordable Annual Premium",
      description: `Single price for all families: ${formatPrice('USD')} (${formatPrice('CFA')} / ${formatPrice('GMD')}) per year, per child. No urban/rural distinction. Early sign-ups receive a 1,200 CFA discount.`,
      details: [
        `${formatPrice('USD')} (${formatPrice('CFA')} / ${formatPrice('GMD')}) per year, per child`,
        "One price for all - no urban/rural distinction",
        "Early sign-up discount: 1,200 CFA",
        "Coverage for children ages 2-18"
      ],
      icon: CreditCard,
      color: "accent"
    },
    {
      number: "03",
      title: "Coverage Activation & ID Card",
      description: "Each child receives an AMHAS ID card with biometric verification. The card is used to validate coverage at both AMHAS Urgent Care Facilities and for AI + telehealth services at school.",
      details: [
        "Each child receives an AMHAS ID card with biometric verification",
        "Biometric matching validates coverage instantly",
        "Used at AMHAS Urgent Care Facilities and for school-based AI + telehealth",
        "100% coverage at AMHAS Urgent Care Facilities",
        "AI + Telehealth services available at school"
      ],
      icon: IdCard,
      color: "secondary"
    },
    {
      number: "04",
      title: "Care Delivery - Two Pathways",
      description: "Care is delivered through two pathways: (1) At AMHAS Urgent Care Facilities with 100% coverage, or (2) In the classroom via AI + Telehealth. At school, vitals/symptoms are captured; AI proposes a diagnosis; a U.S./EU/UK doctor reviews and finalizes care. As we scale, urgent care centers are being established while AI and telehealth expand to all schools.",
      details: [
        "Pathway 1: AMHAS Urgent Care Facilities - 100% coverage, 24/7 access",
        "Pathway 2: Classroom AI + Telehealth - Vitals captured, AI diagnosis, Western doctor review",
        "Biometric ID validation at intake for both pathways",
        "Approximately 90% of cases handled by AI overseen by overseas medical doctor",
        "Referral to AMHAS Urgent Care when care exceeds what can be done at school",
        "As we scale, urgent care centers established while AI/telehealth expands"
      ],
      icon: Stethoscope,
      color: "primary"
    },
    {
      number: "05",
      title: "Urgent Care Facilities (When Needed)",
      description: "If care requires procedures beyond what can be handled at school via AI + Telehealth, children are referred to AMHAS Urgent Care Facilities. These world-class facilities provide 100% coverage for all services including lab work, radiology, observation beds, and non-surgical emergency care.",
      details: [
        "Children present AMHAS ID card with biometric verification at urgent care",
        "100% coverage for all services at AMHAS Urgent Care Facilities",
        "Services include: lab work, radiology, pharmacy, observation beds, non-surgical emergency care",
        "24/7 access, 365 days a year",
        "World-class facilities with Western-standard care",
        "No additional cost beyond annual enrollment fee"
      ],
      icon: Shield,
      color: "accent"
    },
    {
      number: "06",
      title: "Prevention First",
      description: "AMHAS focuses on keeping children healthy, not just treating illness. Regular school health talks on hygiene, nutrition, hydration, and exercise. Safe water initiatives, vaccination campaigns, and early detection programs.",
      details: [
        "Regular school health talks on hygiene, nutrition, hydration, and exercise",
        "Safe water initiatives (filters, purification, individual drinking cups)",
        "Vaccination campaigns and deworming programs",
        "Early detection of issues like malnutrition or vision problems",
        "Reduces illness frequency, hospitalisations, and long-term complications"
      ],
      icon: Heart,
      color: "secondary"
    }
  ];

  const exclusions = [
    "Chronic and terminal illnesses",
    "Major or orthopedic surgeries",
    "Hospitalisation longer than 3 months",
    "Pandemic-related illnesses",
    "Cosmetic procedures",
    "Experimental treatments"
  ];

  const callouts = [
    {
      icon: Clock,
      title: "Fast Turnaround",
      description: "Claims processed within 24-48 hours"
    },
    {
      icon: Globe,
      title: "Multi-language Support",
      description: "Services available in English, French, and local languages"
    },
    {
      icon: Users,
      title: "Child Safeguarding",
      description: "Strict protocols to protect children's safety and privacy"
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/urgentcare_telehealth_classrom.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">How It Works</h1>
            <p className="text-xl md:text-2xl text-primary-100">
              Simple steps to protect your child's health through school-based enrollment
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section className="relative section-padding bg-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="/urgentcare_front_desk.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="section-title">School-Based Enrollment</h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              AMHAS makes healthcare accessible by providing with schools to enroll children aged 2-18.
              This approach ensures that every child in our provider schools has access to affordable
              preventive healthcare, regardless of their family's financial situation.
            </p>
            <div className="bg-primary-50 rounded-lg p-6">
              <p className="text-primary-800 font-semibold">
                <strong>Eligibility:</strong> Children aged 2-18 enrolled in AMHAS provider schools
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Separator Banner */}
      <ImageSeparator 
        image="/urgentcare_nurse_triage_1.png"
        title="The AMHAS Process"
        subtitle="Five simple steps to comprehensive healthcare coverage"
      />

      {/* Steps */}
      <section className="relative section-padding bg-gray-50 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="/urgentcare_telehealth_classrom.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">The AMHAS Process</h2>
            <p className="section-subtitle">
              Five simple steps to comprehensive healthcare coverage
            </p>
          </motion.div>

          <div className="space-y-12">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isEven = index % 2 === 0;

              return (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:grid-flow-col-dense' : ''
                    }`}
                >
                  <div className={`${!isEven ? 'lg:col-start-2' : ''}`}>
                    <div className="card p-8">
                      <div className="flex items-center mb-6">
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center mr-4 ${step.color === 'primary' ? 'bg-primary-100' :
                          step.color === 'accent' ? 'bg-accent-100' :
                            'bg-secondary-100'
                          }`}>
                          <Icon size={32} className={
                            step.color === 'primary' ? 'text-primary-600' :
                              step.color === 'accent' ? 'text-accent-600' :
                                'text-secondary-600'
                          } />
                        </div>
                        <div>
                          <span className="text-2xl font-bold text-gray-400">{step.number}</span>
                          <h3 className="text-2xl font-bold text-gray-900">{step.title}</h3>
                        </div>
                      </div>

                      <p className="text-gray-600 text-lg mb-6">{step.description}</p>

                      <ul className="space-y-3">
                        {step.details.map((detail, detailIndex) => (
                          <li key={detailIndex} className="flex items-start space-x-3">
                            <CheckCircle size={16} className="text-accent-600 mt-1 flex-shrink-0" />
                            <span className="text-gray-700">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className={`${!isEven ? 'lg:col-start-1' : ''}`}>
                    <img 
                      src={
                        step.number === "01" ? "/urgentcare_telehealth_classrom.png" :
                        step.number === "02" ? "/urgentcare_payments_community.png" :
                        step.number === "03" ? "/urgentcare_scan_card_get_care.png" :
                        step.number === "04" ? "/urgentcare_doctor_Care_1.png" :
                        step.number === "05" ? "/urgentcare_back_building.png" :
                        step.number === "06" ? "/urgentcare_lab_work_1.png" :
                        ""
                      }
                      alt={step.title}
                      className="w-full h-64 object-cover rounded-lg"
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Separator Banner */}
      <ImageSeparator 
        image="/urgentcare_doctor_Care_1.png"
        title="Comprehensive Healthcare Coverage"
        subtitle="Understanding what's covered and what's not helps families make informed decisions"
      />

      {/* What's Excluded */}
      <section className="relative section-padding bg-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="/urgentcare_lab_work_1.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="section-title text-center">What's Excluded</h2>
            <p className="section-subtitle text-center">
              Understanding what's not covered helps set clear expectations
            </p>

            <div className="card p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {exclusions.map((exclusion, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">{exclusion}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Telehealth Details */}
      <section className="relative section-padding bg-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="/urgentcare_nurse_triage_1.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Telehealth With Western Doctors</h2>
            <p className="section-subtitle">
              What that means for your child's healthcare
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Comprehensive Telehealth Services</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Video size={24} className="text-primary-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Board-Certified Physicians</h4>
                    <p className="text-gray-600">U.S./EU/UK board-certified physicians available for non-emergency video/audio visits and asynchronous consults (photos, messages).</p>
                  </div>
                </div>


                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-secondary-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone size={24} className="text-secondary-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Low-Bandwidth Modes</h4>
                    <p className="text-gray-600">Voice-only, chat, and store-and-forward for areas with limited internet connectivity.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Globe size={24} className="text-primary-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Language Support</h4>
                    <p className="text-gray-600">English + local interpreters where available; clear, simple instructions for families and schools.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-primary-600 to-accent-600 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">Why This Matters</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle size={20} className="text-white mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">Families</h4>
                      <p className="text-sm text-primary-100">Get timely, expert advice without traveling long distances</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle size={20} className="text-white mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">Local Clinicians</h4>
                      <p className="text-sm text-primary-100">Get global backup and support for complex cases</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle size={20} className="text-white mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">Children</h4>
                      <p className="text-sm text-primary-100">Are treated sooner—often avoiding hospital stays</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 p-4 bg-white/20 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <AlertTriangle size={20} className="text-yellow-300" />
                    <span className="font-semibold">Important Note</span>
                  </div>
                  <p className="text-sm text-primary-100">
                    Telehealth is not for emergencies. In an emergency, use local emergency numbers or go to the nearest facility immediately.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Separator Banner */}
      <ImageSeparator 
        image="/urgentcare_front_desk.png"
        title="Key Features That Make AMHAS Different"
        subtitle="Fast turnaround, multi-language support, and child safeguarding protocols"
      />

      {/* Callouts */}
      <section className="relative section-padding bg-gray-50 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="/urgentcare_payments_community.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Key Features</h2>
            <p className="section-subtitle">
              What makes AMHAS different and effective
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {callouts.map((callout, index) => {
              const Icon = callout.icon;
              return (
                <motion.div
                  key={callout.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="card p-8 text-center"
                >
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon size={32} className="text-primary-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{callout.title}</h3>
                  <p className="text-gray-600">{callout.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTAs */}
      <section className="section-padding bg-primary-600 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              Join thousands of families who have already enrolled their children in AMHAS.
              Find a provider school near you or learn how to bring AMHAS to your school.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Link to="/schools" className="btn-secondary">
                Find a Provider School
              </Link>
              <Link to="/urgent-care" className="btn-secondary">
                Learn About Urgent Care Centers
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
